import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FaCouch, FaFileExcel, FaRegClock, FaSun } from 'react-icons/fa';
import { Dropdown, MenuItem } from 'react-bootstrap';

import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import Button from '@/shared/components/Button';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import ToastrService from '@/shared/services/ToastrService';
import { mapResourceName } from '@/shared/services/ObjectDisplayService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import SignalRService from '@/shared/services/SignalRService';
import DataCacheService from '@/shared/services/DataCacheService';
import handleHttpError from '@/shared/services/HandleHttpError';
import HoverToggle from '@/shared/components/HoverToggle';
import AuthService from '@/shared/services/AuthService';
import { injectStateService, StateService } from '@/shared/services/StateService';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { absenceRegistrationOptions } from '@/shared/components/registration/registrationHelpers';
import messages from '../reportingMessages';
import HourColumn from './HourColumn';
import AbsencePerEmployeeFilters, { defaultFilters } from './AbsencePerEmployeeFilters';
import DaysColumn from './DaysColumn';

const SessionStorageKey = 'AbsencePerEmployeeReportParams';

class AbsencePerEmployeeContainer extends Component {
  state = {
    filters: { ...defaultFilters },
    report: null,
    showRegistrationModal: null,
  };

  constructor(props) {
    super(props);
    const { searchParams = {} } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment.utc(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment.utc(sessionParams.dateUntil);
    }

    this.state = {
      ...this.state,
      filters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
    };
    this.timeTablesEnabled = DataCacheService.timeTablesEnabled();
    this.currentUserId = AuthService.getUserId();
    this.hasRegistrationPermission = AuthService.hasPermission({
      functionality: 'RegistrationOwn',
      privilege: 'Create',
    });
    this.hasRegistrationOthersPermission = AuthService.hasPermission({
      functionality: 'RegistrationOthers',
      privilege: 'Create',
    });
  }

  componentDidMount() {
    this.retrieve();
    SignalRService.subscribe('yearTimeTableUpdated', this.handleYearTimeTableUpdatedEvent);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('yearTimeTableUpdated', this.handleYearTimeTableUpdatedEvent);
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  filtersChanged = filters => {
    this.setState(
      state => ({
        ...state,
        filters: {
          ...state.filters,
          ...filters,
        },
        report: null,
      }),
      () => this.retrieve(),
    );
  };

  getParams = () => {
    const { filters } = this.state;
    return filters;
  };
  retrieve = () => {
    const { filters } = this.state;
    const { intl } = this.props;

    const params = this.getParams();

    const sessionParams = JSON.stringify(filters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();

    return api.base
      .post(`${config.appUrls.reporting}/api/absenceperemployee`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data: { columns = [], rows = [], ...totals } = {} }) => {
        this.setState(state => ({
          ...state,
          report: {
            columns: columns
              .map(col => ({
                ...col,
                label: mapResourceName(col, intl, false, true),
              }))
              .sort(dynamicSort('label')),
            rows,
            ...totals,
          },
        }));
        return true;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          ToastrService.error();
        }
      });
  };
  createExcelReport = () => {
    const { intl } = this.props;

    const reportParams = this.getParams();
    const languageCode = intl.locale;
    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 8,
      parameters: JSON.stringify(reportParams),
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  handleYearTimeTableUpdatedEvent = eventParams => {
    const { userId, dateFrom, dateUntil } = eventParams;
    if (!userId) return;
    const { report, filters } = this.state;
    if (filters.dateFrom && moment.utc(dateUntil).isBefore(moment.utc(filters.dateFrom))) return;
    if (filters.dateUntil && moment.utc(dateFrom).isAfter(moment.utc(filters.dateUntil))) return;
    if (!report || !report.rows || report.rows.findIndex(r => r.userId === userId) < 0) return;

    this.retrieve();
  };

  gotoRegistrationOverview = userId => {
    const { stateService } = this.props;
    const { filters: { dateFrom, dateUntil } = {} } = this.state;

    stateService.gotoRegistrationOverview({
      userId,
      dateFrom,
      dateUntil,
      period: 'All',
    });
  };

  createRegistration = (type, userId) => {
    const { filters: { dateFrom } = {} } = this.state;
    this.setState({
      showRegistrationModal: {
        registration: { type, userId, date: moment.utc(dateFrom).toDate() },
        registrationOptions: type === 3 || type === 4 ? absenceRegistrationOptions : {},
      },
    });
  };

  renderRow = row => {
    const {
      report,
      filters: { inDays },
    } = this.state;
    const canCreateRegistration =
      this.currentUserId === row.userId
        ? this.hasRegistrationPermission
        : this.hasRegistrationOthersPermission;

    const AbsenceColumn = inDays ? DaysColumn : HourColumn;
    return (
      <tr key={row.employeeId}>
        <td>
          <Dropdown id={`Dropdown-${row.employeeId}`} className="width100pct">
            <HoverToggle bsRole="toggle" className="pull-right" />
            {row.employeeName}
            <Dropdown.Menu className="unselectable">
              <MenuItem eventKey="0" onSelect={() => this.gotoRegistrationOverview(row.userId)}>
                <i className="fa fa-th fa-fw margin5R" />
                <FormattedMessage {...messages.OpenRegistrationOverview} />
              </MenuItem>{' '}
              {canCreateRegistration ? (
                <MenuItem eventKey="2" onSelect={() => this.createRegistration(0, row.userId)}>
                  <FaRegClock className="react-icon fa-fw margin5R" />
                  <FormattedMessage {...messages.AddRegistration} />
                </MenuItem>
              ) : null}
              {canCreateRegistration ? (
                <MenuItem eventKey="3" onSelect={() => this.createRegistration(3, row.userId)}>
                  <FaSun className="react-icon fa-fw margin5R" />
                  <FormattedMessage {...messages.AddAbsence} />
                </MenuItem>
              ) : null}
              {canCreateRegistration && DataCacheService.timeTablesEnabled() ? (
                <MenuItem eventKey="4" onSelect={() => this.createRegistration(4, row.userId)}>
                  <FaCouch className="react-icon fa-fw margin5R" />
                  <FormattedMessage {...messages.AddRecuperation} />
                </MenuItem>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        </td>

        {(report.absence || 0) > 0 &&
          report.columns.map(col => <AbsenceColumn value={row.values[col.id]} key={col.id} />)}
        {(report.absence || 0) > 0 && <AbsenceColumn value={row.absence} className="border" />}
        <HourColumn value={row.work} />

        {report.work !== report.total && <HourColumn value={row.total} />}

        {this.timeTablesEnabled && (report.expected || 0) > 0 && (
          <HourColumn value={row.expected} />
        )}

        {this.timeTablesEnabled && (report.missing || 0) > 0 && <HourColumn value={row.missing} />}

        {this.timeTablesEnabled && (report.overtime || 0) > 0 && (
          <HourColumn value={row.overtime} />
        )}
        {(report.overtimeCorrection || 0) !== 0 && <HourColumn value={row.overtimeCorrection} />}

        {this.timeTablesEnabled && (report.recup || 0) > 0 && <HourColumn value={row.recup} />}

        {this.timeTablesEnabled && <HourColumn value={row.overtimeRemaining} />}
      </tr>
    );
  };

  render() {
    const { intl } = this.props;
    const { filters, report, showRegistrationModal } = this.state;

    const hasAbsences = ((report && report.absence) || 0) > 0;

    const AbsenceColumn = filters.inDays ? DaysColumn : HourColumn;

    return (
      <div className="absence-per-employee">
        <SimplePanel
          article="5434869523346"
          title={intl.formatMessage(messages.AbsencePerEmployee)}
        >
          <div className="info-block container-fluid">
            <AbsencePerEmployeeFilters filters={filters} onFilterChange={this.filtersChanged} />
          </div>
        </SimplePanel>

        {report && report.rows && report.rows.length > 0 && (
          <>
            <div className="row btn-row">
              <div className="col-md-6 pull-right content-right">
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.createExcelReport()}
                  showProgress
                >
                  <span className="margin5R">
                    <FaFileExcel className="file-icon" />
                  </span>
                  {intl.formatMessage(messages.ExportToExcel)}
                </Button>
              </div>
            </div>
            <table className="table table-report">
              <thead>
                <tr>
                  <th rowSpan="2">
                    <FormattedMessage {...messages.Employee} />
                  </th>
                  {hasAbsences && (
                    <th className="text-center" colSpan={report.columns.length + 1}>
                      <FormattedMessage {...messages.AbsenceHours} />
                    </th>
                  )}
                  <th className="text-right hours" rowSpan="2">
                    <FormattedMessage {...messages.WorkedHours} />
                  </th>
                  {report.work !== report.total && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.FilledInHours} />
                    </th>
                  )}
                  {this.timeTablesEnabled && (report.expected || 0) > 0 && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.ExpectedHours} />
                    </th>
                  )}
                  {this.timeTablesEnabled && (report.missing || 0) > 0 && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.MissingHours} />
                    </th>
                  )}
                  {this.timeTablesEnabled && (report.overtime || 0) > 0 && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.OvertimeHours} />
                    </th>
                  )}
                  {(report.overtimeCorrection || 0) !== 0 && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.OvertimeCorrectionHours} />
                    </th>
                  )}
                  {(report.recup || 0) > 0 && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.RecuperationHours} />
                    </th>
                  )}

                  {this.timeTablesEnabled && (
                    <th className="text-right hours" rowSpan="2">
                      <FormattedMessage {...messages.RemainingOvertimeHours} />
                    </th>
                  )}
                </tr>
                {hasAbsences && (
                  <tr>
                    {report.columns.map(col => (
                      <th key={col.id} className="text-right hours">
                        {col.label}
                      </th>
                    ))}
                    <th className="text-right hours">
                      <FormattedMessage {...messages.Total} />
                    </th>
                  </tr>
                )}
              </thead>
              <tbody>{report.rows.map(this.renderRow)}</tbody>
              <tfoot>
                <tr className="totals">
                  <td>
                    <FormattedMessage {...messages.Totals} />
                  </td>
                  {hasAbsences &&
                    report.columns.map(col => <AbsenceColumn value={col.total} key={col.id} />)}
                  {hasAbsences && <AbsenceColumn value={report.absence} className="border" />}
                  <HourColumn value={report.work} />
                  {report.work !== report.total && <HourColumn value={report.total} />}

                  {this.timeTablesEnabled && (report.expected || 0) > 0 && (
                    <HourColumn value={report.expected} />
                  )}

                  {this.timeTablesEnabled && (report.missing || 0) > 0 && (
                    <HourColumn value={report.missing} />
                  )}
                  {this.timeTablesEnabled && (report.overtime || 0) > 0 && (
                    <HourColumn value={report.overtime} />
                  )}
                  {(report.overtimeCorrection || 0) !== 0 && (
                    <HourColumn value={report.overtimeCorrection} />
                  )}
                  {this.timeTablesEnabled && (report.recup || 0) > 0 && (
                    <HourColumn value={report.recup} />
                  )}

                  {this.timeTablesEnabled && <HourColumn value={report.overtimeRemaining} />}
                </tr>
              </tfoot>
            </table>
          </>
        )}
        {report && (!report.rows || !report.rows.length) && (
          <div>
            <FormattedMessage {...messages.NoDataForReport} />
          </div>
        )}
        {!report && <LoadingAnimation />}
        {showRegistrationModal && (
          <RegistrationModal
            {...showRegistrationModal}
            show
            onSave={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
            onClose={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
          />
        )}
      </div>
    );
  }
}

AbsencePerEmployeeContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(AbsencePerEmployeeContainer));
